import React, { useRef, useEffect } from 'react';
import GwTaskItem from './GwTaskItem';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

/** @typedef { import('../../../app/services/TaskService.js').TaskColumnObj } TaskColumnObj */
/** @typedef { import('../../../app/models/TaskLinkModel.js').TaskLinkItem } TaskLinkItem */
/** @typedef { import('../../../app/models/TaskBoardModel.js').TaskBoardItem } TaskBoardItem */

/**
 * @typedef {object} GwTaskColumnProps
 * @property {Array<TaskLinkItem>} taskLinks - Liste des liens de tâches
 * @property {TaskColumnObj} taskColumn - Colonne de tâches
 * @property {function(number):void} onClickOnDeleteBtn - Fonction appelée lors du clic sur le bouton de suppression d'une tâche
 * @property {function():any} onClickOnResetBtn - Fonction appelée lors du clic sur le bouton de réinitialisation des tâches
 * @property {function(string, object):void} onDateChange - Fonction appelée lors du changement de date
 * @property {function():void} onClickOnSetDeadlineDateToItem - Fonction appelée lors du clic sur le bouton pour définir la date limite des tâches par rapport à la date de début de l'item
 * @property {function():void} onClickOnDeleteDoneTasks - Fonction appelée lors du clic sur le bouton pour supprimer toutes les tâches terminées
 * @property {boolean} canBeReinitialized - Indique si les tâches peuvent être réinitialisées
 * @property {string} autoDeadlineDateAttribute - Attribut de date limite automatique
 * @property {?TaskBoardItem} taskBoard - Identifiant du tableau de tâches
 * @property {string} dataId - Identifiant de l'élément lié aux tâches
 * @property {string} model - Modèle de l'élément lié aux tâches
 * @property {?TaskLinkItem} movableTaskLink - Tâche en cours de déplacement (avec sa colonne de départ)
 * @property {string} avatarApiUrl - URL de l'API pour les avatars des utilisateurs
 */

/**
 * @param {GwTaskColumnProps} props
 */
export default function GwTaskColumn({taskLinks, taskColumn, onClickOnDeleteBtn, onClickOnResetBtn, onDateChange, onClickOnSetDeadlineDateToItem, onClickOnDeleteDoneTasks, canBeReinitialized, autoDeadlineDateAttribute, taskBoard, dataId, model, movableTaskLink, avatarApiUrl}) {
  
  var datePickerInputRef = /** @type {React.RefObject<any>}*/(useRef(null)),
    datePickerRef = /** @type {React.RefObject<any>}*/(useRef(null)),
    onDateChangeRef = /** @type {React.RefObject<any>}*/(useRef(onDateChange)),
    classes = ['task__list'],
    { setNodeRef, over } = useDroppable({ id: `column-${taskColumn.id}`, data: { taskColumnId: taskColumn.id } }),
    overTaskColumnId = /** @type {any} */(over?.data?.current)?.taskColumnId,
    isDraggedOverFromOtherColumn = !!movableTaskLink && overTaskColumnId === taskColumn.id && movableTaskLink.state !== taskColumn.id,
    allDone = taskLinks.every(taskLink => taskLink.state === 2),
    doneTaskLinks = taskLinks.filter(taskLink => taskLink.state === 2),
    toDoTaskLinks = taskLinks.filter(taskLink => taskLink.state === 0),
    addHref = `/_add/task-link/?onlyfields=userId,description,notes,external_link,deadline_date&model=${model}&data_id=${dataId}`;

  useEffect(() => {
    /**
     * @param {any} event
     */
    const onChangeDate = event => {
      onDateChangeRef.current(event.detail.date, datePickerInputRef);
    };

    if (datePickerInputRef.current) {
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }

      var options = {
        language: 'fr',
        autohide: true,
        clearBtn: true,
        todayBtn: true,
        todayHighlight: true,
        todayBtnMode: 1,
        format: 'dd/mm/yyyy',
        container: document.body
      };

      // @ts-ignore
      datePickerRef.current = new Datepicker(datePickerInputRef.current, options);

      datePickerInputRef.current.addEventListener('changeDate', onChangeDate);
    }

    return () => {
      if (datePickerInputRef.current) {
        datePickerInputRef.current.removeEventListener('changeDate', onChangeDate);
      }
    };
  }, [datePickerInputRef.current]);
  
  if (isDraggedOverFromOtherColumn) {
    classes.push('task__list--dragged');
  }

  return (
    <div ref={setNodeRef} className={classes.join(' ')}>

      <h5 className="task__list-title">
        {taskColumn.name}
        <div className="task__list-title-actions">
          {taskColumn.name === 'To Do' && toDoTaskLinks.length && autoDeadlineDateAttribute ? (
            <button className="btn btn--no-label btn--only-svg" data-tooltip="Date limte des tâches par rapport à la date de début de l'item" onClick={() => onClickOnSetDeadlineDateToItem()}>
              <span className="material-symbols-outlined">calendar_clock</span>
            </button>
          ) : (
            ''
          )}
          {taskColumn.name === 'To Do' ? (
            <a className="btn btn--no-label btn--only-svg" href={addHref} data-tooltip="Ajouter une tâche">
              <span className="material-icons">add</span>
            </a>
          ) : (
            ''
          )}
          {taskColumn.name === 'Done' && doneTaskLinks.length ? (
            <button className="btn btn--only-svg btn--only-svg-danger-color" data-tooltip="Supprimer toutes les tâches terminées" onClick={() => onClickOnDeleteDoneTasks()}>
              <span className="material-icons">delete</span>
            </button>
          ) : (
            ''
          )}
        </div>
      </h5>
      <div className="task__list-tasks">
        <SortableContext items={taskColumn.taskLinks.map(taskLink => taskLink.id)} strategy={verticalListSortingStrategy}>
          {taskColumn.taskLinks.map((taskLink) => (
            <GwTaskItem
              key={taskLink.id}
              taskLink={taskLink}
              taskColumn={taskColumn}
              onClickOnDeleteBtn={() => onClickOnDeleteBtn(taskLink.id)}
              isDraggingOverlay={false}
              avatarApiUrl={avatarApiUrl}
            />
          ))}
        </SortableContext>
      </div>
      {taskColumn.name === 'Done' && taskLinks.length && allDone && canBeReinitialized ? (
        <div>
          <button className="btn btn--full" onClick={() => onClickOnResetBtn()}>Réinitialiser les tâches</button>
        </div>
      ) : (
        ''
      )}
    </div>
  )
}
